import React from "react";
import {
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  CheckboxField,
} from "@aws-amplify/ui-react";
import { I18n } from "aws-amplify";
import { AiOutlineDownload, AiOutlinePrinter } from "react-icons/ai";
import { BsArrowUpShort, BsArrowDownShort } from "react-icons/bs";

const SearchResults = ({
  results,
  downloadFile,
  downloadZip,
  printPDF,
  printMergedPDFs,
  selectedFiles,
  setSelectedFiles,
  onSortChange,
  sortCriteria,
}) => {
  // handles different file downloads
  const fileSelectLimit = 100;

  const handleDownload = async (e, result) => {
    const isTestMode = process.env.REACT_APP_TEST === "true";
    const fileType = e.target.value;
    const filePaths = {
      pdf: `PDFFiles/${
        isTestMode ? "pdfFile-test.pdf" : result._source.PDFFileName
      }`,
      signature: `SignatureFiles/${
        isTestMode
          ? "signatureFiles-test.pdf.pkcs7"
          : result._source.SignatureFileName
      }`,
      report: `SignatureReports/${
        isTestMode
          ? "signatureReport-test.html"
          : result._source.SignatureReportName
      }`,
      idoc: `IDOCFiles/${isTestMode ? "IDOC-test.idoc" : result._source.IDOC}`,
      printPdf: result._source.PDFFileName,
    };

    if (filePaths[fileType]) {
      if (fileType === "printPdf") {
        printPDF(filePaths[fileType]);
      } else {
        downloadFile(filePaths[fileType]);
      }
    } else {
      console.warn("Unknown option selected");
    }
    e.target.value = "default";
  };

  const handlePrintSelectedPDFs = async () => {
    const pdfFileNames = selectedFiles.map((file) => file.pdf);
    await printMergedPDFs(pdfFileNames);
  };

  // Convert contentType Name for result table to display
  const getContentTypeDisplay = (contentType) => {
    switch (contentType) {
      case "credit note":
        return I18n.get("Credit note");
      case "consi credit note":
        return I18n.get("Consi credit note");
      case "debit note":
        return I18n.get("Debit note");
      default:
        return contentType;
    }
  };

  const getStateTypeDisplay = (documentState) => {
    switch (documentState) {
      case "1":
        return I18n.get("New");
      case "2":
        return I18n.get("Read");
      case "3":
        return I18n.get("Downloaded");
      default:
        return documentState;
    }
  };

  function getBrowserLanguage() {
    return navigator.language || navigator.userLanguage;
  }

  //display different date format based on language
  function formatDate(originalDateFormat) {
    const datePart = originalDateFormat.split("T")[0];
    const [year, month, day] = datePart.split("-");

    const language = getBrowserLanguage();

    if (language.startsWith("de")) {
      return `${day}.${month}.${year}`; // German format
    } else {
      return `${month}/${day}/${year}`; // Default to English format
    }
  }

  //display different icons based on sort asc or desc
  const renderSortIcon = (columnName) => {
    if (sortCriteria.column === columnName) {
      return sortCriteria.order === "asc" ? (
        <BsArrowUpShort className="up-icon" />
      ) : (
        <BsArrowDownShort className="down-icon" />
      );
    }
    return null;
  };

  const SortableHeader = ({ columnName, label }) => (
    <TableCell
      as="th"
      className="table-title"
      onClick={() => onSortChange(columnName)}
    >
      {I18n.get(label)} {renderSortIcon(columnName)}
    </TableCell>
  );

  return (
    <Table className="result-table" variation="striped" size="small">
      <TableHead>
        <TableRow>
          <TableCell as="th" colSpan="1">
            <AiOutlineDownload
              className="download-icon"
              onClick={downloadZip}
            />
            <AiOutlinePrinter
              className="download-icon"
              onClick={handlePrintSelectedPDFs}
            ></AiOutlinePrinter>
          </TableCell>
          <TableCell as="th">{I18n.get("Type")}</TableCell>
          <SortableHeader columnName="PDFFileName" label="Document name" />
          <SortableHeader columnName="UmsatzsteuerID" label="Tax Id" />
          <SortableHeader
            columnName="Lieferantennummer"
            label="Supplier Number"
          />
          <SortableHeader columnName="GS_Nummer" label="Credit Note No." />
          <SortableHeader
            columnName="interneGS_Nr"
            label="Internal Credit Note No."
          />
          <SortableHeader columnName="Ausstellungsdatum" label="Date" />
          <SortableHeader columnName="eBoxAmount" label="Amount" />
          <SortableHeader columnName="ValeoWerk" label="Valeo Company" />
          <SortableHeader columnName="DUNS" label="DUNS Number" />
          <SortableHeader columnName="Herkunftsland" label="Country From" />
          <SortableHeader columnName="Zielland" label="Country To" />
          <SortableHeader columnName="ViewStatus" label="Document state" />
          <SortableHeader columnName="ContentType" label="Content type" />
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((result) => (
          <TableRow className="result-items" key={result._id}>
            <TableCell>
              <div
                onClick={() => {
                  if (
                    selectedFiles.length >= fileSelectLimit &&
                    !selectedFiles.some(
                      (file) => file.pdf === result._source.PDFFileName
                    )
                  ) {
                    window.alert(
                      I18n.get( "You cannot select more than ") +
                        fileSelectLimit +
                        I18n.get( " files!")
                    );
                  }
                }}
              >
                <CheckboxField
                  name="subscribe"
                  value={JSON.stringify({
                    pdf: result._source.PDFFileName,
                    signature: result._source.SignatureFileName,
                    report: result._source.SignatureReportName,
                    idoc: result._source.IDOC,
                  })}
                  checked={selectedFiles.some(
                    (file) => file.pdf === result._source.PDFFileName
                  )}
                  disabled={
                    selectedFiles.length >= fileSelectLimit &&
                    !selectedFiles.some(
                      (file) => file.pdf === result._source.PDFFileName
                    )
                  }
                  onChange={(e) => {
                    const fileData = JSON.parse(e.target.value);
                    if (e.target.checked) {
                      setSelectedFiles((prevFiles) => [...prevFiles, fileData]);
                    } else {
                      setSelectedFiles((prevFiles) =>
                        prevFiles.filter((file) => file.pdf !== fileData.pdf)
                      );
                    }
                  }}
                />
              </div>
            </TableCell>
            <TableCell>
              <select
                onChange={(e) => handleDownload(e, result)}
                defaultValue="default"
              >
                <option value="default" disabled>
                  {I18n.get("Select file type")}
                </option>
                <option value="pdf">PDF</option>
                <option value="signature">{I18n.get("Signature")}</option>
                <option value="report">
                  {I18n.get("Verification report")}
                </option>
                <option value="idoc">{I18n.get("IDOC")}</option>
                <option value="printPdf">{I18n.get("Print")}</option>
              </select>
            </TableCell>
            <TableCell>
              {result._source.PDFFileName.replace(".pdf", "")}
            </TableCell>
            <TableCell>{result._source.UmsatzsteuerID}</TableCell>
            <TableCell>{result._source.Lieferantennummer}</TableCell>
            <TableCell>{result._source.GS_Nummer}</TableCell>
            <TableCell>{result._source.interneGS_Nr}</TableCell>
            <TableCell>
              {formatDate(result._source.Ausstellungsdatum)}
            </TableCell>
            <TableCell>{result._source.eBoxAmount}</TableCell>
            <TableCell>{result._source.ValeoWerk}</TableCell>
            <TableCell>{result._source.DUNS}</TableCell>
            <TableCell>{result._source.Herkunftsland}</TableCell>
            <TableCell>{result._source.Zielland}</TableCell>
            <TableCell>
              {getStateTypeDisplay(result._source.ViewStatus)}
            </TableCell>
            <TableCell>
              {getContentTypeDisplay(result._source.ContentType)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default SearchResults;
